import { Button, Dialog, Input, Select } from '@artosera/ui';
import type { TypeContact } from '@artosera/shared';
import { useOptionsTypeContact } from '../../api/typesContact.js';
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCreerContact, type GenreContact } from '../../api/contacts.js';
import { Field } from '../Field.js';
import { ChampsManquants } from '../ChampsManquants.js';
import { useErreurFormulaire } from '../useErreurFormulaire.js';
import { useFermetureGardee } from '../useFermetureGardee.js';
import { LIMITES, erreurEmail, erreurTelephone } from '../validations.js';
import { Monogramme } from '../../composants/Monogramme.js';
import styles from './NouveauContactDialog.module.css';

export interface NouveauContactDialogProps {
  galerieSlug: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCree: (id: string) => void;
}

/**
 * Dialogue de création du carnet (3.3, écran 3, S3-c). Le genre choisit le
 * champ d'identité — nom pour une personne, raison sociale pour une
 * organisation (`nomAffiche`, `apps/api/src/contacts/commun.ts`).
 */
export function NouveauContactDialog({
  galerieSlug,
  open,
  onOpenChange,
  onCree,
}: NouveauContactDialogProps): JSX.Element {
  const { t } = useTranslation(['regie', 'contacts', 'commun']);

  const [genre, setGenre] = useState<GenreContact>('personne');
  const [nom, setNom] = useState('');
  const [prenom, setPrenom] = useState('');
  const [raisonSociale, setRaisonSociale] = useState('');
  const [type, setType] = useState<TypeContact>('collectionneur');
  const [email, setEmail] = useState('');
  const [telephone, setTelephone] = useState('');
  const formulaire = useErreurFormulaire();

  const typesContact = useOptionsTypeContact(galerieSlug);
  const creer = useCreerContact(galerieSlug);

  async function soumettre(): Promise<void> {
    formulaire.reinitialiser();
    if (erreurEmail(email) || erreurTelephone(telephone)) return;
    try {
      const cree = await creer.mutateAsync({
        genre,
        nom: genre === 'organisation' ? raisonSociale.trim() || nom.trim() : nom.trim(),
        ...(prenom.trim() ? { prenom: prenom.trim() } : {}),
        ...(genre === 'organisation' ? { raisonSociale: raisonSociale.trim() } : {}),
        type,
        ...(email.trim() ? { emails: [email.trim()] } : {}),
        ...(telephone.trim() ? { telephones: [telephone.trim()] } : {}),
      });
      onOpenChange(false);
      onCree(cree.contact.id);
    } catch (error) {
      formulaire.appliquer(error, ['nom', 'prenom', 'raisonSociale', 'emails', 'telephones']);
    }
  }

  const identiteValide =
    genre === 'organisation' ? raisonSociale.trim().length > 0 : nom.trim().length > 0;
  const cleErreurEmail = erreurEmail(email);
  const cleErreurTelephone = erreurTelephone(telephone);
  const peutSoumettre =
    identiteValide && !cleErreurEmail && !cleErreurTelephone && !creer.isPending;
  const garde = useFermetureGardee(
    [nom, prenom, raisonSociale, email, telephone].some((valeur) => valeur.trim().length > 0),
    onOpenChange,
  );
  const nomCarte =
    genre === 'organisation'
      ? raisonSociale.trim()
      : [prenom.trim(), nom.trim()].filter(Boolean).join(' ');

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={garde.onOpenChange}
        title={t('regie:contacts.nouveauContact.titre')}
        closeLabel={t('commun:actions.fermer')}
        footer={
          <>
            <Button variant="ghost" onClick={() => garde.onOpenChange(false)}>
              {t('commun:actions.annuler')}
            </Button>
            <Button
              onClick={() => void soumettre()}
              loading={creer.isPending}
              disabled={!peutSoumettre}
            >
              {t('regie:contacts.nouveauContact.creer')}
            </Button>
          </>
        }
      >
        <form className={styles.formulaire} onSubmit={(event) => event.preventDefault()} noValidate>
          {/* Carte de visite en cours de composition : reflet visuel de la
            saisie, ignoré des lecteurs d'écran (les champs disent tout). */}
          <div className={styles.carte} aria-hidden>
            <Monogramme nom={nomCarte} />
            <span className={styles.carteTexte}>
              <span className={styles.carteNom} data-vide={nomCarte ? 'false' : 'true'}>
                {nomCarte}
              </span>
              <span className={styles.carteType}>{typesContact.libelle(type)}</span>
              <span className={styles.carteLigne}>{email.trim()}</span>
              <span className={styles.carteLigne}>{telephone.trim()}</span>
            </span>
          </div>

          <Field label={t('regie:contacts.nouveauContact.genreChamp')} htmlFor="contact-genre">
            <Select
              id="contact-genre"
              aria-label={t('regie:contacts.nouveauContact.genreChamp')}
              value={genre}
              onValueChange={(valeur) => setGenre(valeur as GenreContact)}
              options={[
                { value: 'personne', label: t('regie:contacts.nouveauContact.genrePersonne') },
                {
                  value: 'organisation',
                  label: t('regie:contacts.nouveauContact.genreOrganisation'),
                },
              ]}
            />
          </Field>

          {genre === 'organisation' ? (
            <Field
              label={t('regie:contacts.nouveauContact.raisonSocialeChamp')}
              htmlFor="contact-raison-sociale"
              error={formulaire.messageChamp('raisonSociale')}
            >
              <Input
                id="contact-raison-sociale"
                maxLength={LIMITES.contact.raisonSociale}
                required
                value={raisonSociale}
                onChange={(event) => setRaisonSociale(event.target.value)}
              />
            </Field>
          ) : null}

          <div className={styles.grille2}>
            <Field
              label={t('regie:contacts.nouveauContact.nomChamp')}
              htmlFor="contact-nom"
              error={formulaire.messageChamp('nom')}
            >
              <Input
                id="contact-nom"
                maxLength={LIMITES.contact.nom}
                required={genre === 'personne'}
                value={nom}
                onChange={(event) => setNom(event.target.value)}
              />
            </Field>
            <Field
              label={t('regie:contacts.nouveauContact.prenomChamp')}
              htmlFor="contact-prenom"
              error={formulaire.messageChamp('prenom')}
            >
              <Input
                id="contact-prenom"
                maxLength={LIMITES.contact.prenom}
                value={prenom}
                onChange={(event) => setPrenom(event.target.value)}
              />
            </Field>
          </div>

          <Field label={t('regie:contacts.nouveauContact.typeChamp')} htmlFor="contact-type">
            <Select
              id="contact-type"
              aria-label={t('regie:contacts.nouveauContact.typeChamp')}
              value={type}
              onValueChange={(valeur) => setType(valeur as TypeContact)}
              options={typesContact.options}
            />
          </Field>

          <div className={styles.grille2}>
            <Field
              label={t('regie:contacts.nouveauContact.emailChamp')}
              htmlFor="contact-email"
              error={
                email.trim() && cleErreurEmail
                  ? t(cleErreurEmail)
                  : formulaire.messageChamp('emails')
              }
            >
              <Input
                id="contact-email"
                maxLength={LIMITES.contact.email}
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </Field>
            <Field
              label={t('regie:contacts.nouveauContact.telephoneChamp')}
              htmlFor="contact-telephone"
              error={
                telephone.trim() && cleErreurTelephone
                  ? t(cleErreurTelephone)
                  : formulaire.messageChamp('telephones')
              }
            >
              <Input
                id="contact-telephone"
                maxLength={LIMITES.contact.telephone}
                type="tel"
                value={telephone}
                onChange={(event) => setTelephone(event.target.value)}
              />
            </Field>
          </div>

          <ChampsManquants
            champs={
              identiteValide
                ? []
                : [
                    genre === 'organisation'
                      ? t('regie:contacts.nouveauContact.raisonSocialeChamp')
                      : t('regie:contacts.nouveauContact.nomChamp'),
                  ]
            }
          />
          {formulaire.erreur ? (
            <p className={styles.erreur} role="alert">
              {formulaire.erreur}
            </p>
          ) : null}
        </form>
      </Dialog>
      {garde.confirmation}
    </>
  );
}
